import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Handles Google's redirect back to LocaNest: `/auth/google?code=...&state=...` (LocaNest's own OAuth client).
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const processed = useRef(false);
  const [needsRole, setNeedsRole] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const qs = new URLSearchParams(location.search);
  const code = qs.get("code");
  const state = qs.get("state");
  const googleError = qs.get("error");

  const finish = (data) => {
    sessionStorage.removeItem("ln_google_role"); sessionStorage.removeItem("ln_google_ref"); sessionStorage.removeItem("ln_google_state");
    applySession(data.token, data.user);
    toast.success(data.is_new ? `Welcome to LocaNest, ${data.user.name.split(" ")[0]}!` : `Welcome back, ${data.user.name.split(" ")[0]}!`);
    navigate(data.user.role === "admin" ? "/admin" : data.user.role === "owner" ? "/owner" : "/listings", { replace: true });
  };

  const exchange = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/google/callback", {
        code,
        redirect_uri: window.location.origin + "/auth/google",
        role: sessionStorage.getItem("ln_google_role") || null,
        referral_code: sessionStorage.getItem("ln_google_ref") || null,
      });
      window.history.replaceState(null, "", "/auth/google");
      if (data.needs_role) { setNeedsRole(data); setBusy(false); return; }
      finish(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const complete = async (role) => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/google/complete", { pending_id: needsRole.pending_id, role, referral_code: sessionStorage.getItem("ln_google_ref") || null });
      finish(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    if (googleError) { setError(googleError === "access_denied" ? "Google sign-in was cancelled." : `Google sign-in failed (${googleError}).`); return; }
    if (!code) { navigate("/login", { replace: true }); return; }
    const expected = sessionStorage.getItem("ln_google_state");
    if (expected && state !== expected) { setError("Sign-in session mismatch. Please try again."); return; }
    exchange();
    // eslint-disable-next-line
  }, []);

  if (error) {
    return (
      <div className="max-w-md mx-auto px-5 py-20 text-center" data-testid="auth-callback-error">
        <div className="text-red-600 text-sm">{error}</div>
        <button onClick={() => navigate("/login", { replace: true })} className="mt-4 text-sm text-[#C85A32] underline">Back to login</button>
      </div>
    );
  }

  if (needsRole) {
    return (
      <div className="max-w-md mx-auto px-5 py-16" data-testid="auth-role-picker">
        <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Almost there</div>
        <h1 className="font-display text-3xl mt-2">Hi {needsRole.name?.split(" ")[0] || "there"} — how will you use LocaNest?</h1>
        <p className="text-sm text-[#5C6B7A] mt-2">Signing up as <strong>{needsRole.email}</strong>. You can&rsquo;t change this later, so pick carefully.</p>
        <div className="mt-6 grid gap-3">
          <button disabled={busy} onClick={() => complete("tenant")} className="text-left rounded-lg border border-[#1A1F2C]/15 bg-[#F0EBE1] hover:border-[#C85A32] p-4 transition-colors" data-testid="auth-role-tenant">
            <div className="font-semibold">I&rsquo;m looking for a rental (Tenant)</div>
            <div className="text-xs text-[#5C6B7A] mt-1">Browse verified listings, save favourites, chat with owners.</div>
          </button>
          <button disabled={busy} onClick={() => complete("owner")} className="text-left rounded-lg border border-[#1A1F2C]/15 bg-[#F0EBE1] hover:border-[#C85A32] p-4 transition-colors" data-testid="auth-role-owner">
            <div className="font-semibold">I want to list a property (Owner / Broker)</div>
            <div className="text-xs text-[#5C6B7A] mt-1">Publish listings, get verified, manage inquiries and payouts.</div>
          </button>
        </div>
      </div>
    );
  }

  return <div className="p-16 text-center text-sm text-[#5C6B7A]" data-testid="auth-callback-loading">Signing you in with Google…</div>;
}
